import React, { useState } from 'react';
import { Modal, Typography, Form, Input, Checkbox, Button, Divider, Row, Col, message } from 'antd';
import styles from './OpenAccount.module.scss';
import { useSelector } from 'react-redux';
import { getUserMeta } from 'shared/config/store/reducers/userSlice';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { useGetCountriesQuery } from 'entities/countries/api';
import {
  useCreatePlanMainAccountMutation,
  useCreatePlanAccountMutation,
} from 'entities/account/api';
import { OnfidoVerification } from 'features/account/add-account/ui/OnfidoVerification';
import { getCurrentEmi } from 'shared/config/store/selectors/app';
import { getCountryName, getCountryPhoneCode } from 'entities/countries/model/utils';
import { getLocalizedLinks } from '../model/const';

interface OpenAccountProps {
  open: boolean;
  onClose: () => void;
}

const OpenAccount: React.FC<OpenAccountProps> = ({ open, onClose }) => {
  const [form] = Form.useForm();
  const { formatMessage: t, locale } = useIntl();

  const emi = useSelector(getCurrentEmi);
  const user = useSelector(getUserMeta);
  const { data: countriesResponse } = useGetCountriesQuery();
  const countries = countriesResponse?.response ?? [];

  const country = getCountryName(user.address.country, countries);
  const phoneCountry = getCountryPhoneCode(user.phoneCountry, countries);

  const { privacyLink, termsLink } = getLocalizedLinks(locale);

  const [createMainAccount, { isLoading: isMainLoading }] = useCreatePlanMainAccountMutation();
  const [createAccount, { isLoading: isAccountLoading }] = useCreatePlanAccountMutation();

  const [verificationModalOpen, setVerificationModalOpen] = useState(false);

  const acceptPrivacy = Form.useWatch('acceptPrivacy', form);
  const acceptTerms = Form.useWatch('acceptTerms', form);
  const isSubmitDisabled = !acceptPrivacy || !acceptTerms;


  const handleSubmit = async () => {
    try {
      await createMainAccount({ emi: emi }).unwrap();
      setVerificationModalOpen(true);
    } catch (error: any) {
      if (error?.errorFields) {
      } else {
        message.error( t({ id: 'openAccount.modal.message.error' }) );
      }
    }
  };

  const handleVerificationComplete = async () => {
    try {
      await createAccount({ emi: emi, title: 'My Account' }).unwrap();
      message.success( t({ id: 'openAccount.modal.message.success' }) );
      setVerificationModalOpen(false);
      onClose();
    } catch (error: any) {
      message.error( t({ id: 'openAccount.modal.message.error' }) );
    }
  };

  return (
    <>
      <Modal
        title={<Typography.Title level={3} style={{ margin: 0 }}>{ t({ id: 'openAccount.modal.title' }) }</Typography.Title>}
        open={open}
        onCancel={onClose}
        footer={null}
        width={1000}
        mask={!verificationModalOpen}
        maskClosable={!verificationModalOpen}
      >
        <Typography.Text type="secondary">
          { t({ id: 'openAccount.modal.subtitle' }) }
        </Typography.Text>

        <Divider />

        <Form layout="vertical" form={form}>
          <Row gutter={24}>
            <Col xs={24} md={8}>
              <Typography.Title level={5}>{ t({ id: 'openAccount.modal.info.title.general' }) }</Typography.Title>
              <Form.Item label={ t({ id: 'openAccount.modal.info.name' }) }>
                <Input disabled value={user.firstName} />
              </Form.Item>
              <Form.Item label={ t({ id: 'openAccount.modal.info.surname' }) }>
                <Input disabled value={user.lastName} />
              </Form.Item>
              <Form.Item label={ t({ id: 'openAccount.modal.info.date' }) }>
                <Input disabled value={user.dateOfBirth} />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Typography.Title level={5}>{ t({ id: 'openAccount.modal.info.title.contact' }) }</Typography.Title>
              <Form.Item label={ t({ id: 'openAccount.modal.info.email' }) }>
                <Input disabled value={user.email} />
              </Form.Item>
              <Form.Item label={ t({ id: 'openAccount.modal.info.phone' }) }>
                <Input disabled value={`+${phoneCountry} ${user.phone}`} />
              </Form.Item>
              <Form.Item label={ t({ id: 'openAccount.modal.info.country' }) }>
                <Input disabled value={country} />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Typography.Title level={5}>&nbsp;</Typography.Title>
              <Form.Item label={ t({ id: 'openAccount.modal.info.address' }) }>
                <Input disabled value={user.address.line1} />
              </Form.Item>
              <Form.Item label={ t({ id: 'openAccount.modal.info.city' }) }>
                <Input disabled value={user.address.city} />
              </Form.Item>
              <Form.Item label={ t({ id: 'openAccount.modal.info.postal' }) }>
                <Input disabled value={user.address.postalCode} />
              </Form.Item>
            </Col>
          </Row>

          <div className={styles.infoBox}>
            <InfoCircleOutlined className={styles.iconStyle} />
            <Typography.Text type="secondary">
              { t({ id: 'openAccount.modal.information' }) }
            </Typography.Text>
          </div>

          <Form.Item
            name="acceptPrivacy"
            valuePropName="checked"
            rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject('You must accept this terms') }]}
          >
            <Checkbox>
              { t({ id: 'openAccount.modal.link.agree' }) }
              <Typography.Link href={privacyLink} target="_blank">
                {t({ id: 'openAccount.modal.link.privacyPolicy' })}
              </Typography.Link>
            </Checkbox>
          </Form.Item>

          <Form.Item
            name="acceptTerms"
            valuePropName="checked"
            rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject('You must accept this terms') }]}
          >
            <Checkbox>
              { t({ id: 'openAccount.modal.link.agree' }) }
              <Typography.Link href={termsLink} target="_blank">
                {t({ id: 'openAccount.modal.link.termsConditions' })}
              </Typography.Link>
            </Checkbox>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              block
              onClick={handleSubmit}
              loading={isMainLoading || isAccountLoading}
              disabled={isSubmitDisabled}
            >
              {t({ id: 'openAccount.modal.button' })}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={verificationModalOpen}
        footer={null}
        centered
        onCancel={() => setVerificationModalOpen(false)}
        closable={true}
        mask={true}
        zIndex={1001}
        getContainer={false}
      >
        <OnfidoVerification
          emi={emi}
          onComplete={handleVerificationComplete}
          closeVerificationModal={() => setVerificationModalOpen(false)}
        />
      </Modal>
    </>
  );
};

export default OpenAccount;
